import React, { useState, useEffect } from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
  Button,
  Text,
  Box,
  VStack,
  HStack,
  Heading,
  Divider,
  useToast,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  Icon,
  Select,
  Input,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { FaShoppingCart, FaEuroSign } from 'react-icons/fa';
import axios from 'axios';
import PaymentForm from '../PaymentForm';
import { useAuth } from '../../contexts/AuthContext';
import {
  canPurchaseMerch,
  getPreorderMessage,
  isMerchPreorder,
} from '../../utils/merchFulfillmentLabels';
import {
  EMPTY_MERCH_SHIPPING_ADDRESS,
  isMerchShippingAddressComplete,
} from '../../utils/merchShipping';
import { calculateMerchCheckoutTotal } from '../../utils/merchCheckoutPricing';
import {
  buildMerchCheckoutSession,
  buildMerchSuccessSummary,
  clearMerchCheckoutSession,
  extractOrderDocumentId,
  saveMerchCheckoutSession,
  submitMerchOrder,
} from '../../utils/merchOrderCheckout';

// Inizializza Stripe.js
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC, {
  locale: 'it',
});

const ProductCheckoutDrawer = ({ isOpen, onClose, product, onSuccess }) => {
  const { userData, requestUserData } = useAuth();
  const toast = useToast();
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [localPaymentIntentId, setLocalPaymentIntentId] = useState('');
  const [availableDiscount, setAvailableDiscount] = useState(null);
  const [fulfillmentMethod, setFulfillmentMethod] = useState('pickup');
  const [deliveryConfirmed, setDeliveryConfirmed] = useState(false);
  const [shippingAddress, setShippingAddress] = useState({
    ...EMPTY_MERCH_SHIPPING_ADDRESS,
  });

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Estrai le caratteristiche disponibili dal prodotto
  const getAvailableVariants = () => {
    if (!product?.caratteristiche) return {};

    const caratteristiche =
      typeof product.caratteristiche === 'string'
        ? JSON.parse(product.caratteristiche)
        : product.caratteristiche;

    const variants = {};

    // Cerca taglie, colori e altre varianti comuni
    const variantKeys = [
      'taglia',
      'size',
      'colore',
      'color',
      'colori',
      'colors',
      'modello',
      'model',
    ];

    Object.keys(caratteristiche).forEach((key) => {
      const lowerKey = key.toLowerCase();
      if (variantKeys.some((vk) => lowerKey.includes(vk))) {
        const value = caratteristiche[key];
        if (
          Array.isArray(value) ||
          (typeof value === 'string' && value.includes(','))
        ) {
          variants[key] = Array.isArray(value)
            ? value
            : value.split(',').map((v) => v.trim());
        } else if (typeof value === 'object') {
          variants[key] = value;
        }
      }
    });

    return variants;
  };

  const availableVariants = getAvailableVariants();

  const isShippingAddressComplete =
    fulfillmentMethod !== 'shipping' ||
    isMerchShippingAddressComplete(shippingAddress);

  // Helper per normalizzare un ID (converte in stringa per il confronto)
  const normalizeId = (id) => {
    if (id === null || id === undefined) return null;
    return String(id);
  };

  // Helper per calcolare lo sconto applicabile a un prodotto
  const getProductDiscount = (productId, discounts) => {
    if (!discounts || discounts.length === 0 || !productId) return null;

    const normalizedProductId = normalizeId(productId);

    const applicableDiscounts = discounts.filter((discount) => {
      // Se lo sconto ha type = 'all-products', si applica a tutti i prodotti
      if (discount.type === 'all-products') {
        return true;
      }
      // Se lo sconto ha type = 'products', verifica se si applica a questo prodotto specifico
      if (
        discount.type === 'products' &&
        discount.productIds &&
        Array.isArray(discount.productIds)
      ) {
        return discount.productIds.some(
          (id) => normalizeId(id) === normalizedProductId
        );
      }
      return false;
    });

    if (applicableDiscounts.length === 0) return null;

    // Restituisci lo sconto con la percentuale più alta
    return applicableDiscounts.reduce((best, current) =>
      current.percentageAmount > best.percentageAmount ? current : best
    );
  };

  // Recupera gli sconti disponibili quando il drawer si apre
  useEffect(() => {
    const fetchDiscounts = async () => {
      if (!isOpen || !product || !userData) {
        setAvailableDiscount(null);
        return;
      }

      try {
        const response = await fetch('/api/user/discounts');
        if (response.ok) {
          const data = await response.json();
          const discounts = data.discounts || [];
          const productId = product.documentId || product.id;
          const discount = getProductDiscount(productId, discounts);
          setAvailableDiscount(discount);
        }
      } catch (err) {
        console.error('Errore nel caricamento degli sconti:', err);
        setAvailableDiscount(null);
      }
    };

    fetchDiscounts();
  }, [isOpen, product, userData]);

  // Inizializza le varianti selezionate quando il drawer si apre
  useEffect(() => {
    if (isOpen && product) {
      const variants = getAvailableVariants();
      const initial = {};
      Object.keys(variants).forEach((key) => {
        const [firstOption] = variants[key];
        if (Array.isArray(variants[key]) && variants[key].length > 0) {
          initial[key] = firstOption;
        }
      });
      setSelectedVariants(initial);
    } else {
      // Reset quando il drawer si chiude
      setSelectedVariants({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const createPaymentIntent = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!product || (!product.id && !product.documentId)) {
        throw new Error('Prodotto non valido');
      }

      if (!canPurchaseMerch(product, 1)) {
        throw new Error("Prodotto non disponibile per l'acquisto");
      }

      const unitPrice = parseFloat(product.prezzo) || 0;
      if (unitPrice <= 0) {
        throw new Error('Prezzo non valido');
      }

      if (fulfillmentMethod === 'shipping' && !isShippingAddressComplete) {
        return;
      }

      const discountPct = availableDiscount?.percentageAmount || 0;
      const pricing = calculateMerchCheckoutTotal({
        product,
        discountPercentage: discountPct,
        fulfillmentMethod,
        quantity: 1,
      });

      // Usa preferibilmente documentId quando disponibile (per Strapi v5)
      const productId = product.documentId || product.id;

      // Chiama l'endpoint per creare il payment intent
      const response = await axios.post(
        `/api/products/${productId}/payment-intent`,
        {
          amount: Math.round(pricing.total * 100),
          currency: 'eur',
          stripeCustomerId: userData?.stripeCustomerId || null,
          variants: selectedVariants,
          discountId: availableDiscount?.id || null,
          discountPercentage: discountPct || null,
          fulfillmentMethod,
          shippingAddress:
            fulfillmentMethod === 'shipping' ? shippingAddress : null,
          quantity: 1,
        },
        {
          withCredentials: true, // Necessario per inviare i cookie di autenticazione
        }
      );

      if (response.data.clientSecret) {
        setClientSecret(response.data.clientSecret);
        const nextLocalPaymentIntentId =
          response.data.localPaymentIntentId || '';
        setLocalPaymentIntentId(nextLocalPaymentIntentId);

        let participantId = null;
        if (userData?.tesseramenti?.length > 0) {
          participantId =
            userData.tesseramenti[0].documentId || userData.tesseramenti[0].id;
        }

        saveMerchCheckoutSession(
          buildMerchCheckoutSession({
            product,
            fulfillmentMethod,
            shippingAddress,
            pricing,
            selectedVariants,
            availableDiscount,
            participantId,
            localPaymentIntentId: nextLocalPaymentIntentId,
          })
        );
      } else {
        throw new Error('Errore nella creazione del payment intent');
      }
    } catch (err) {
      console.error('Error creating payment intent:', err);
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        'Errore nella creazione del payment intent';
      setError(errorMessage);
      toast({
        title: 'Errore',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelivery = () => {
    if (!isShippingAddressComplete) return;
    setDeliveryConfirmed(true);
    createPaymentIntent();
  };

  useEffect(() => {
    if (!deliveryConfirmed || !isOpen || !product) return;
    createPaymentIntent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableDiscount]);

  useEffect(() => {
    setDeliveryConfirmed(false);
    setClientSecret('');
    setError(null);
  }, [fulfillmentMethod]);

  useEffect(() => {
    if (!deliveryConfirmed) return;
    setDeliveryConfirmed(false);
    setClientSecret('');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shippingAddress.line1,
    shippingAddress.city,
    shippingAddress.zip,
    shippingAddress.nome,
    shippingAddress.cognome,
  ]);

  useEffect(() => {
    if (isOpen && userData) {
      setShippingAddress((prev) => ({
        ...prev,
        nome: prev.nome || userData.firstName || '',
        cognome: prev.cognome || userData.lastName || '',
      }));
    }
  }, [isOpen, userData]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedVariants({});
      setClientSecret('');
      setError(null);
      setAvailableDiscount(null);
      setLocalPaymentIntentId('');
      setFulfillmentMethod('pickup');
      setDeliveryConfirmed(false);
      setShippingAddress({ ...EMPTY_MERCH_SHIPPING_ADDRESS });
    }
  }, [isOpen]);

  const buildCheckoutSuccessPayload = (paymentData, pricing, participantId) =>
    buildMerchSuccessSummary({
      session: {
        productId: product.documentId || product.id,
        productName: product.nome,
        fulfillmentMethod,
        shippingAddress:
          fulfillmentMethod === 'shipping' ? shippingAddress : null,
        isPreorder: isMerchPreorder(product, 1),
        total: pricing.total,
        productTotal: pricing.productTotal,
        productSubtotal: pricing.productSubtotal,
        shippingFee: pricing.shippingFee,
        discountAmount: pricing.discountAmount,
        originalAmount: parseFloat(product.prezzo) || 0,
        selectedVariants,
        participantId: participantId || null,
        localPaymentIntentId,
      },
      paymentData,
    });

  const handlePaymentSuccess = async (paymentData) => {
    const productId = product.documentId || product.id;
    const productPrice = parseFloat(product.prezzo) || 0;
    const discountPct = availableDiscount?.percentageAmount || 0;
    const pricing = calculateMerchCheckoutTotal({
      product,
      discountPercentage: discountPct,
      fulfillmentMethod,
      quantity: 1,
    });

    let participantId = null;
    if (userData?.tesseramenti?.length > 0) {
      participantId =
        userData.tesseramenti[0].documentId || userData.tesseramenti[0].id;
    }

    try {
      const orderResponse = await submitMerchOrder({
        productId,
        participantId,
        paymentIntentId: paymentData.paymentIntentId,
        paymentStatus: paymentData.paymentStatus || 'succeeded',
        paymentMethodId: paymentData.paymentMethodId,
        localPaymentIntentId,
        pricing,
        productName: product.nome,
        originalAmount: productPrice,
        availableDiscount,
        selectedVariants,
        fulfillmentMethod,
        shippingAddress,
      });

      const orderDocumentId = extractOrderDocumentId(orderResponse);
      const successPayload = buildCheckoutSuccessPayload(
        {
          ...paymentData,
          orderDocumentId,
          localPaymentIntentId,
          createdAt: new Date().toISOString(),
        },
        pricing,
        participantId
      );

      if (requestUserData) {
        requestUserData({ withLoading: false });
      }

      clearMerchCheckoutSession();

      if (onSuccess) {
        onSuccess(successPayload);
      }
      onClose();
    } catch (err) {
      console.error('Error creating order:', err);
      toast({
        title: 'Errore',
        description:
          err.response?.data?.error ||
          "Errore nella creazione dell'ordine. Il pagamento è stato completato ma l'ordine non è stato creato. Contatta il supporto.",
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      clearMerchCheckoutSession();
      if (onSuccess) {
        onSuccess(
          buildCheckoutSuccessPayload(
            {
              ...paymentData,
              createdAt: new Date().toISOString(),
            },
            pricing
          )
        );
      }
      onClose();
    }
  };

  const handlePaymentError = (paymentError) => {
    toast({
      title: 'Errore nel pagamento',
      description:
        paymentError.message ||
        'Si è verificato un errore durante il pagamento',
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
  };

  const productPrice = parseFloat(product?.prezzo) || 0;
  const discountPct = availableDiscount?.percentageAmount || 0;
  const checkoutPricing = product
    ? calculateMerchCheckoutTotal({
        product,
        discountPercentage: discountPct,
        fulfillmentMethod,
        quantity: 1,
      })
    : {
        productSubtotal: 0,
        discountAmount: 0,
        productTotal: 0,
        shippingFee: 0,
        total: 0,
      };
  const {
    discountAmount,
    productTotal: finalPrice,
    shippingFee,
    total: orderTotal,
  } = checkoutPricing;
  const isPreorder = product ? isMerchPreorder(product, 1) : false;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="md" placement="right">
      <DrawerOverlay />
      <DrawerContent bg={bgColor}>
        <DrawerCloseButton />
        <DrawerHeader borderBottom="1px" borderColor={borderColor}>
          <HStack spacing={2}>
            <Icon as={FaShoppingCart} />
            <Heading size="md">Checkout</Heading>
          </HStack>
        </DrawerHeader>

        <DrawerBody>
          <VStack spacing={6} align="stretch">
            {/* Riepilogo Prodotto */}
            {product && (
              <Box
                p={4}
                bg={useColorModeValue('gray.50', 'gray.700')}
                borderRadius="md"
                border="1px"
                borderColor={borderColor}
              >
                <VStack spacing={3} align="stretch">
                  <Heading size="sm">{product.nome}</Heading>

                  {product.descrizioneBreve && (
                    <Text fontSize="sm" color="gray.600">
                      {product.descrizioneBreve}
                    </Text>
                  )}

                  <Divider />

                  {/* Sconto disponibile */}
                  {availableDiscount &&
                    availableDiscount.percentageAmount > 0 && (
                      <Box
                        p={2}
                        bg={useColorModeValue('green.50', 'green.900')}
                        borderRadius="md"
                        border="1px"
                        borderColor="green.200"
                      >
                        <HStack justify="space-between">
                          <Text
                            fontSize="sm"
                            fontWeight="semibold"
                            color="green.700"
                          >
                            Sconto {availableDiscount.tierName}:
                          </Text>
                          <Text
                            fontSize="sm"
                            fontWeight="bold"
                            color="green.700"
                          >
                            -{availableDiscount.percentageAmount}%
                          </Text>
                        </HStack>
                      </Box>
                    )}

                  {/* Prezzo */}
                  <VStack spacing={1} align="stretch">
                    {discountAmount > 0 && (
                      <HStack justify="space-between">
                        <Text
                          fontSize="sm"
                          color="gray.500"
                          textDecoration="line-through"
                        >
                          Prezzo originale:
                        </Text>
                        <HStack spacing={1}>
                          <Icon as={FaEuroSign} size="xs" />
                          <Text
                            fontSize="sm"
                            color="gray.500"
                            textDecoration="line-through"
                          >
                            {productPrice.toFixed(2)}
                          </Text>
                        </HStack>
                      </HStack>
                    )}
                    <HStack justify="space-between">
                      <Text fontWeight="semibold">
                        {discountAmount > 0 ? 'Prezzo scontato:' : 'Prezzo:'}
                      </Text>
                      <HStack spacing={1}>
                        <Icon as={FaEuroSign} />
                        <Text
                          fontSize="lg"
                          fontWeight="bold"
                          color={discountAmount > 0 ? 'green.600' : 'inherit'}
                        >
                          {finalPrice.toFixed(2)}
                        </Text>
                      </HStack>
                    </HStack>
                    {fulfillmentMethod === 'shipping' && (
                      <HStack justify="space-between">
                        <Text fontWeight="semibold">Spedizione:</Text>
                        <HStack spacing={1}>
                          <Icon as={FaEuroSign} />
                          <Text fontSize="md" fontWeight="semibold">
                            {shippingFee > 0
                              ? shippingFee.toFixed(2)
                              : 'Gratuita'}
                          </Text>
                        </HStack>
                      </HStack>
                    )}
                    {(fulfillmentMethod === 'shipping' ||
                      discountAmount > 0) && (
                      <HStack justify="space-between" pt={1}>
                        <Text fontWeight="bold">Totale:</Text>
                        <HStack spacing={1}>
                          <Icon as={FaEuroSign} />
                          <Text fontSize="xl" fontWeight="bold">
                            {orderTotal.toFixed(2)}
                          </Text>
                        </HStack>
                      </HStack>
                    )}
                  </VStack>
                </VStack>
              </Box>
            )}

            <Box
              p={4}
              bg={useColorModeValue('gray.50', 'gray.700')}
              borderRadius="md"
              border="1px"
              borderColor={borderColor}
            >
              <VStack spacing={4} align="stretch">
                <Heading size="sm">Consegna</Heading>
                <FormControl>
                  <FormLabel>Metodo</FormLabel>
                  <Select
                    value={fulfillmentMethod}
                    onChange={(e) => setFulfillmentMethod(e.target.value)}
                  >
                    <option value="pickup">Ritiro in sede / evento</option>
                    <option value="shipping">Spedizione</option>
                  </Select>
                </FormControl>
                {fulfillmentMethod === 'shipping' && (
                  <>
                    <HStack align="flex-start">
                      <FormControl isRequired>
                        <FormLabel>Nome</FormLabel>
                        <Input
                          value={shippingAddress.nome}
                          onChange={(e) =>
                            setShippingAddress({
                              ...shippingAddress,
                              nome: e.target.value,
                            })
                          }
                        />
                      </FormControl>
                      <FormControl isRequired>
                        <FormLabel>Cognome</FormLabel>
                        <Input
                          value={shippingAddress.cognome}
                          onChange={(e) =>
                            setShippingAddress({
                              ...shippingAddress,
                              cognome: e.target.value,
                            })
                          }
                        />
                      </FormControl>
                    </HStack>
                    <FormControl isRequired>
                      <FormLabel>Indirizzo</FormLabel>
                      <Input
                        value={shippingAddress.line1}
                        onChange={(e) =>
                          setShippingAddress({
                            ...shippingAddress,
                            line1: e.target.value,
                          })
                        }
                      />
                    </FormControl>
                    <HStack>
                      <FormControl isRequired>
                        <FormLabel>Città</FormLabel>
                        <Input
                          value={shippingAddress.city}
                          onChange={(e) =>
                            setShippingAddress({
                              ...shippingAddress,
                              city: e.target.value,
                            })
                          }
                        />
                      </FormControl>
                      <FormControl isRequired>
                        <FormLabel>CAP</FormLabel>
                        <Input
                          value={shippingAddress.zip}
                          onChange={(e) =>
                            setShippingAddress({
                              ...shippingAddress,
                              zip: e.target.value,
                            })
                          }
                        />
                      </FormControl>
                    </HStack>
                  </>
                )}
              </VStack>
            </Box>

            {isPreorder && (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  {getPreorderMessage(fulfillmentMethod)}
                </Text>
              </Alert>
            )}

            {/* Selezione Varianti (Taglie, Colori, etc.) */}
            {Object.keys(availableVariants).length > 0 && (
              <Box
                p={4}
                bg={useColorModeValue('gray.50', 'gray.700')}
                borderRadius="md"
                border="1px"
                borderColor={borderColor}
              >
                <VStack spacing={4} align="stretch">
                  <Heading size="sm">Seleziona opzioni</Heading>

                  {Object.keys(availableVariants).map((variantKey) => {
                    const options = availableVariants[variantKey];
                    if (!Array.isArray(options) || options.length === 0)
                      return null;

                    return (
                      <FormControl key={variantKey}>
                        <FormLabel
                          fontSize="sm"
                          fontWeight="semibold"
                          textTransform="capitalize"
                        >
                          {variantKey}
                        </FormLabel>
                        <Select
                          value={selectedVariants[variantKey] || ''}
                          onChange={(e) => {
                            setSelectedVariants({
                              ...selectedVariants,
                              [variantKey]: e.target.value,
                            });
                          }}
                          size="md"
                        >
                          {options.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                    );
                  })}
                </VStack>
              </Box>
            )}

            <Divider />
            <Heading size="sm">Pagamento elettronico</Heading>

            {!deliveryConfirmed ? (
              <Box>
                {fulfillmentMethod === 'shipping' &&
                  !isShippingAddressComplete && (
                    <Text fontSize="sm" color="gray.600" mb={3}>
                      Compila nome, cognome e indirizzo di spedizione, poi
                      conferma la consegna per procedere al pagamento.
                    </Text>
                  )}
                <Button
                  colorScheme="teal"
                  width="full"
                  onClick={handleConfirmDelivery}
                  isDisabled={!isShippingAddressComplete}
                >
                  Conferma consegna e procedi al pagamento
                </Button>
              </Box>
            ) : (
              <>
                {loading && (
                  <Box textAlign="center" py={8}>
                    <Spinner size="xl" />
                    <Text mt={4} color="gray.600">
                      Preparazione pagamento...
                    </Text>
                  </Box>
                )}

                {!loading && error && (
                  <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    <VStack align="stretch" spacing={3} flex={1}>
                      <Text fontSize="sm">{error}</Text>
                      <Button size="sm" onClick={handleConfirmDelivery}>
                        Riprova
                      </Button>
                    </VStack>
                  </Alert>
                )}

                {!loading && !error && clientSecret && (
                  <Box>
                    <Elements
                      stripe={stripePromise}
                      options={{
                        clientSecret,
                        locale: 'it',
                      }}
                    >
                      <PaymentForm
                        ambito="merch"
                        onSuccess={handlePaymentSuccess}
                        onError={handlePaymentError}
                      />
                    </Elements>
                  </Box>
                )}
              </>
            )}
          </VStack>
        </DrawerBody>

        <DrawerFooter borderTop="1px" borderColor={borderColor}>
          <Button variant="outline" mr={3} onClick={onClose}>
            Annulla
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default ProductCheckoutDrawer;
